import { Button } from "@babackapp/customer-ui/components/button";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  BannerToEmbedMessage,
  CartItem,
  EmbedToBannerMessage,
  ShopNowLabels,
} from "@babackapp/return-widget-components/types/shop-now";

type ShopNowState = {
  orderId: string;
  exchangeCreditAmount: number;
  currency: string;
  /** 1 + tax rate for HT shops; 1 for TTC shops. Scale cartTotal before comparing. */
  taxMultiplier: number;
  lang?: string;
  labels?: ShopNowLabels;
};

// The banner has no i18n: the widget sends its texts translated (BBS-404).
// These are only for sessions saved before that, or an older widget.
const FALLBACK_LABELS: ShopNowLabels = {
  checkoutButton: "Continuer le retour",
  creditToSpend: "Vous avez {amount} de crédit échange à dépenser",
  creditLeft: "{cart} dans le panier · {remaining} de crédit restant",
  amountToPay: "{cart} dans le panier · {due} à payer",
  continue: "Continuer",
};

// `lang` comes from the widget's i18n (`zh-hant`, `pt-br`…). A tag Intl
// rejects falls back to the browser's locale rather than crashing the banner.
function currencyFormatter(lang: string | undefined, currency: string) {
  const options = { style: "currency", currency, minimumFractionDigits: 2 } as const;
  try {
    return new Intl.NumberFormat(lang, options);
  } catch {
    return new Intl.NumberFormat(undefined, options);
  }
}

function fill(template: string, values: Record<string, string>) {
  return template.replace(/\{(\w+)\}/g, (token, name: string) => values[name] ?? token);
}

function postToParent(msg: BannerToEmbedMessage) {
  window.parent.postMessage(msg, "*");
}

export function RebuyBanner() {
  const { shopId } = useParams<{ shopId: string }>();
  const [shopNowState, setShopNowState] = useState<ShopNowState | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    postToParent({ type: "BANNER_READY" });
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToBannerMessage;
      if (!msg?.type) return;

      if (msg.type === "SHOP_NOW_INIT") {
        setShopNowState({
          orderId: msg.orderId,
          exchangeCreditAmount: msg.exchangeCreditAmount,
          currency: msg.currency,
          taxMultiplier: msg.taxMultiplier ?? 1,
          lang: msg.lang,
          labels: msg.labels,
        });
      }

      if (msg.type === "CART_UPDATE") {
        setCartItems(msg.items);
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Scale cartItems prices (HT from Shopify /cart.js on HT shops) to TTC using taxMultiplier,
  // so that remaining = TTC credit - TTC cart — consistent with exchangeCreditAmount (TTC).
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity * (shopNowState?.taxMultiplier ?? 1),
    0,
  );

  const handleComplete = () => {
    if (!shopNowState) return;
    postToParent({
      type: "SHOP_NOW_COMPLETE",
      items: cartItems,
      orderId: shopNowState.orderId,
    });
  };

  const handleCancel = () => {
    postToParent({ type: "SHOP_NOW_CANCEL" });
  };

  if (!shopNowState) {
    return <div data-baback-banner data-shop-id={shopId} />;
  }

  const { exchangeCreditAmount, currency, lang } = shopNowState;
  const labels = shopNowState.labels ?? FALLBACK_LABELS;
  const formatter = currencyFormatter(lang, currency);
  const fmt = (cents: number) => formatter.format(cents / 100);

  const remaining = exchangeCreditAmount - cartTotal;

  // Above the credit the customer pays the difference: say how much, instead
  // of a "0 credit left" that hides it until the payment screen (BBS-404).
  const label =
    cartTotal <= 0
      ? fill(labels.creditToSpend, { amount: fmt(exchangeCreditAmount) })
      : remaining >= 0
        ? fill(labels.creditLeft, { cart: fmt(cartTotal), remaining: fmt(remaining) })
        : fill(labels.amountToPay, { cart: fmt(cartTotal), due: fmt(-remaining) });

  return (
    <div
      data-baback-banner
      data-shop-id={shopId}
      className="flex items-center gap-2 h-14 px-4 bg-white rounded-full w-full"
    >
      <Button variant="secondary" size="icon" onClick={handleCancel}>
        <Trash2 />
      </Button>

      <span className="flex-1 text-sm font-medium text-center truncate">
        {label}
      </span>

      <Button
        size="sm"
        disabled={cartItems.length === 0}
        onClick={handleComplete}
      >
        {labels.continue}
      </Button>
    </div>
  );
}
